import React, { useMemo, useState, useEffect } from "react";
import { Link, useParams, withRouter } from "react-router-dom";
import { connect } from 'react-redux'
import { useTable, usePagination, useSortBy, useResizeColumns, useFlexLayout } from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from 'react-query'
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import { reducer, useEffectToDispatch, fetchApiData, initialState, Sorting } from "@/shared/apiTableHelper"
import { useDispatch, flash, flashSuccess, flashError } from "@/shared/components/flash"
import ConfirmPopup from "@/shared/components/modal/confirmModal/ConfirmPopup"
import { escapeHtml, toCurrency, isFailedResponse } from "@/shared/helpers"
import axios from "axios";
import {
    Container,
    Card,
    CardBody,
    Row,
    Col,
    Spinner,
} from "reactstrap";

import { REDEMPTION_HOLD_COLUMNS } from "./columns";
import DeclineHoldModal from "@/shared/components/DeclineHoldModal";
import ProgramBreadcrumb from '@/shared/components/ProgramBreadcrumb'
import FullPageLoader from '@/shared/components/FullPageLoader';

const queryClient = new QueryClient()


const DataTable = ({ program, organization, auth }) => {
    const flashDispatcher = useDispatch()
    const [trigger, setTrigger] = useState(Math.floor(Date.now() / 1000));
    const [activeHold, setActiveHold] = useState(null)
    const [isApproveConfirmOpen, setApproveConfirmOpen] = useState(false)
    const [replayConfirmMessage, setReplayConfirmMessage] = useState(null)
    const [isDeclineOpen, setDeclineOpen] = useState(false)
    const [approvingHoldId, setApprovingHoldId] = useState(null)

    const baseUrl = `organization/${organization.id}/program/${program.id}`

    const askApprove = (hold) => {
        setActiveHold(hold)
        setApproveConfirmOpen(true)
    }

    const confirmApprove = (confirmReplay = false) => {
        setApproveConfirmOpen(false)
        setReplayConfirmMessage(null)
        setApprovingHoldId(activeHold.id)
        axios.put(`${baseUrl}/redemption-hold/${activeHold.id}/approve`, confirmReplay ? { confirm_replay: true } : undefined)
            .then((response) => {
                setTrigger(Math.floor(Date.now() / 1000)) // re-sync either way: hold reverts to PENDING server-side on failure
                // The participant bought from the held merchant since the hold; replaying may duplicate it.
                if (response?.data?.confirmation_required) {
                    setReplayConfirmMessage(response.data.message)
                    return
                }
                if (isFailedResponse(response)) {
                    if (!response?.data) flash('warning', flashDispatcher, 'The approval did not complete a response, so its outcome is unknown. The hold has been refreshed - check its status before trying again.')
                    return
                }
                if (response.data.warnings?.length) {
                    flash('warning', flashDispatcher, response.data.warnings.join(' '))
                    return
                }
                flashSuccess(flashDispatcher, 'Redemption approved and processed.')
            })
            .catch(error => flashError(flashDispatcher, error.response?.data))
            .finally(() => setApprovingHoldId(null))
    }

    const openDecline = (hold) => {
        setActiveHold(hold)
        setDeclineOpen(true)
    }

    const toggleDecline = () => setDeclineOpen(prevState => !prevState)

    let redemption_hold_columns = [
        ...REDEMPTION_HOLD_COLUMNS,
        {
            Header: "Actions",
            id: "actions",
            Cell: ({ row }) => {
                const hold = row.original
                // PROCESSING: an approval was started. If it never finished, acting on it
                // again after a few minutes lets the API settle it (approved or back to pending).
                const isProcessing = hold.status === "PROCESSING"
                const isPending = hold.status === "PENDING" || isProcessing
                const isUnderReview = !!hold.user?.is_under_review
                const isApprovingThis = approvingHoldId === hold.id
                return (
                    <div className="d-flex flex-column">
                        <div className="d-flex" style={{ gap: 0, flexWrap: "nowrap" }}>
                            {isPending && (
                                <>
                                    <span
                                        className={`btn btn-sm btn-primary p-1${isApprovingThis ? ' disabled' : ''}`}
                                        style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                        onClick={() => !approvingHoldId && askApprove(hold)}
                                    >
                                        {isApprovingThis ? <>Approving <Spinner size="sm" className="ms-1 align-middle" /></> : 'Approve'}
                                    </span>
                                    <span
                                        className={`btn btn-sm btn-secondary p-1${approvingHoldId ? ' disabled' : ''}`}
                                        style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                        onClick={() => !approvingHoldId && openDecline(hold)}
                                    >Decline</span>
                                </>
                            )}
                            {!isPending && "—"}
                        </div>
                        {isProcessing && <small className="text-muted">Approval in progress</small>}
                        {/* Read-only: clearing this (Mark Redeemer Safe) is a Super Admin action, done from the Admin app's Redemptions review page - not here. */}
                        {isUnderReview && (
                            <small className="text-muted">Account still under review</small>
                        )}
                    </div>
                )
            },
            width: 220,
            minWidth: 220,
        },
    ]

    let columns = useMemo(() => redemption_hold_columns, [baseUrl, approvingHoldId]) // eslint-disable-line react-hooks/exhaustive-deps

    const defaultColumn = React.useMemo(
        () => ({
            minWidth: 30,
            width: 150,
            maxWidth: 400,
        }),
        []
    )

    const [{ queryPageIndex, queryPageSize, totalCount, queryPageSortBy, queryTrigger }, dispatch] = React.useReducer(reducer, initialState);

    const apiUrl = `${baseUrl}/redemption-hold`

    const { isLoading, error, data, isSuccess } = useQuery(
        ['redemption-holds', program.id, queryPageIndex, queryPageSize, queryPageSortBy, queryTrigger],
        () => fetchApiData(
            {
                url: apiUrl,
                page: queryPageIndex,
                size: queryPageSize,
                sortby: queryPageSortBy,
                trigger: queryTrigger
            }).then(result => {
                // The global interceptor resolves a 403 instead of rejecting it.
                if (!Array.isArray(result?.results)) throw new Error('Redemption holds could not be loaded.');
                return result;
            }),
        {
            keepPreviousData: true,
            staleTime: Infinity
        }
    );

    const totalPageCount = Math.ceil(totalCount / queryPageSize)
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        pageCount,
        pageOptions,
        gotoPage,
        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        setPageSize,
        state: { pageIndex, pageSize, sortBy },
    } = useTable({
        columns,
        data: useMemo(() => (data ? data?.results : []), [data]),
        initialState: {
            pageIndex: queryPageIndex,
            pageSize: queryPageSize,
            sortBy: queryPageSortBy,
        },
        manualPagination: true,
        disableSortBy: true, // the API always returns newest first; sorting in memory would only sort the current page
        pageCount: data ? totalPageCount : 0,
        autoResetSortBy: false,
        autoResetPage: false,
        defaultColumn,
    },
        useSortBy,
        usePagination,
        useResizeColumns,
        useFlexLayout,
    );

    useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter: {}, data, useFilter: false, trigger });

    if (error) {
        return <p>Redemption holds could not be loaded, or you do not have access to them.</p>;
    }

    if (isLoading) {
        return <p>Loading...</p>;
    }

    if (!isSuccess) {
        return null;
    }

    return (
        <>
            <FullPageLoader show={!!approvingHoldId} text="Approving redemption..." />
            <div className='table react-table' style={{ overflowX: 'auto' }}>
                <table {...getTableProps()} className="table">
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                        {column.render('Header')}
                                        {column.isSorted ? <Sorting column={column} /> : ''}
                                        <div
                                            {...column.getResizerProps()}
                                            className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                                        />
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="table table--bordered" {...getTableBodyProps()}>
                        {page.length === 0 && (
                            <tr><td colSpan={columns.length}>No redemptions are currently held for review.</td></tr>
                        )}
                        {page.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            {(rows.length > 0) && (
                <ReactTablePagination
                    page={page}
                    gotoPage={gotoPage}
                    previousPage={previousPage}
                    nextPage={nextPage}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageSize={pageSize}
                    pageIndex={pageIndex}
                    pageCount={pageCount}
                    setPageSize={setPageSize}
                    manualPageSize={[]}
                    dataLength={totalCount}
                />
            )}
            <ConfirmPopup
                isOpen={isApproveConfirmOpen}
                toggle={() => setApproveConfirmOpen(false)}
                action={() => confirmApprove()}
                message={activeHold ? `Approve and process the held redemption for ${escapeHtml(activeHold.user?.first_name)} ${escapeHtml(activeHold.user?.last_name)}? <br/>This will complete the original purchase.` : ''}
            />
            <ConfirmPopup
                isOpen={!!replayConfirmMessage}
                toggle={() => setReplayConfirmMessage(null)}
                action={() => confirmApprove(true)}
                message={`${escapeHtml(replayConfirmMessage || '')} <br/>Process the original cart anyway?`}
            />
            {activeHold && (
                <DeclineHoldModal
                    isOpen={isDeclineOpen}
                    toggle={toggleDecline}
                    programId={program.id}
                    holdId={activeHold.id}
                    description={<>{activeHold.user?.first_name} {activeHold.user?.last_name} — {activeHold.merchant?.name} ({toCurrency(activeHold.redemption_value)})</>}
                    onSuccess={() => setTrigger(Math.floor(Date.now() / 1000))}
                />
            )}
        </>
    )
}

const TableWrapper = ({ program, organization, auth }) => {
    return (
        <QueryClientProvider client={queryClient}>
            <DataTable {...{ program, organization, auth }} />
        </QueryClientProvider>
    )
}

const RedemptionHolds = ({ organization, auth }) => {
    const { programId } = useParams();
    const [program, setProgram] = useState(null);

    const fetchProgramData = async (organization) => {
        try {
            const response = await axios.get(`/organization/${organization.id}/program/${programId}`);
            setProgram(response.data)
        } catch (e) {
            throw new Error(`API error:${e?.message}`);
        }
    };

    useEffect(() => {
        if (organization) {
            fetchProgramData(organization)
        }
    }, [organization])

    if (!program?.id || !organization?.id) {
        return 'Loading...'
    }

    return (
        <Container className="dashboard">
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Redemption Holds</h3>
                    <h3 className="page-subhead subhead">
                        <Link to="/">Home</Link> / <Link to="/program">Programs</Link> <ProgramBreadcrumb program={program} linkSelf={true} /> / Redemption Holds
                    </h3>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Card>
                        <CardBody>
                            {auth?.isSuperAdmin
                                ? <TableWrapper {...{ program, organization, auth }} />
                                : <p>Only Super Admins can review redemption holds.</p>}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}

export default withRouter(connect((state) => ({
    organization: state.organization,
    auth: state.auth
}))(RedemptionHolds));
